import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { phoneDigits } from "../lib/phone_digits"
import { syncGateReason } from "../lib/gate_reason"
import { setButtonLoading } from "../lib/button_loading"
import { statusBadgeClass } from "../lib/status_badge"

// Mirrors the view's first paint (user_settings/trust_center/_otp_verification).
const ALREADY_VERIFIED_REASON = "Already verified — use Change Number to verify a new one"

// Sole-proprietor mobile OTP for the Trust Center wizard (epic #6718, slice ②).
// Mirrors phone_verification_inline_controller: send code, verify code, resend —
// all over AJAX, no page reloads. The phone field it reads is also the wizard's
// real mobile_phone input; the code input opts out of the wizard's required-field
// gate via data-wizard-skip. The authoritative gate is server-side
// (TrustCenterStepService) — this controller is UX only.
//
// The element's data-verified-phone holds the number the server verified (blank
// when none). The wizard's Ready Gate (#18683) reads it: the cell counts as
// verified only while the field still holds that number. Change Number unlocks a
// verified field so a different number can be verified.
//
// Buttons keep their label while working (BUTTON-8): busy = disabled +
// aria-busy + .gl-btn-spinner, never a "Sending..." label swap.
export default class extends Controller {
  static targets = [
    "phoneInput", "sendBtn",
    "codeSection", "codeInput", "verifyBtn",
    "badge", "message", "changeLink",
  ]

  static values = {
    sendUrl: String,
    verifyUrl: String,
  }

  async sendCode(event) {
    event.preventDefault()
    const phone = this.phoneInputTarget.value.trim()
    if (!phone) {
      this.showMessage("Enter your mobile number first.", "error")
      return
    }

    this.setBusy(this.sendBtnTarget, true)
    let sent = false

    try {
      const response = await post(this.sendUrlValue, {
        body: JSON.stringify({ mobile_phone: phone }),
        contentType: "application/json",
        responseKind: "json",
      })
      const data = await response.json

      if (data.success) {
        // A new code clears the server's verification of any earlier number.
        this.element.dataset.verifiedPhone = ""
        this.codeSectionTarget.classList.remove("d-none")
        this.codeInputTarget.focus()
        this.showMessage("Code sent! Check your phone.", "success")
        sent = true
      } else {
        this.showMessage(data.message || "Failed to send code.", "error")
      }
    } catch {
      this.showMessage("Something went wrong. Please try again.", "error")
    } finally {
      this.setBusy(this.sendBtnTarget, false)
      // After the busy state restores the button's markup, or it would undo this.
      if (sent) this.setLabel(this.sendBtnTarget, "Resend Code")
    }
  }

  async verifyCode(event) {
    event.preventDefault()
    const code = this.codeInputTarget.value.trim()
    if (code.length !== 6) {
      this.showMessage("Enter the 6-digit code we texted you.", "error")
      return
    }

    this.setBusy(this.verifyBtnTarget, true)

    try {
      const response = await post(this.verifyUrlValue, {
        body: JSON.stringify({ code }),
        contentType: "application/json",
        responseKind: "json",
      })
      const data = await response.json

      if (data.success) {
        this.markVerified()
        // The used code is spent; Change Number → Send Code starts a fresh one.
        this.codeInputTarget.value = ""
        this.showMessage("Mobile number verified!", "success")
      } else {
        this.showMessage(data.message || "Invalid code. Please try again.", "error")
      }
    } catch {
      this.showMessage("Something went wrong. Please try again.", "error")
    } finally {
      // Always released: a Verify left busy after a success was dead for the
      // next number after Change Number until a reload (#19798).
      this.setBusy(this.verifyBtnTarget, false)
    }
  }

  async resendCode(event) {
    event.preventDefault()
    // Disable the Resend link for the duration of the request so it can't be
    // clicked repeatedly before the server-side rate limit kicks in. sendCode
    // manages the outer Send Code button separately.
    // BUTTON-8: the shared spinner + aria-busy, label kept.
    const trigger = event.currentTarget
    if (trigger) this.setBusy(trigger, true)
    try {
      await this.sendCode(event)
    } finally {
      if (trigger) this.setBusy(trigger, false)
    }
  }

  // Change Number (#18683): a verified number stays readonly until the agent
  // asks to change it. Unlock the field and Send Code; the badge keeps reading Verified until the
  // number actually changes (phoneEdited), so backing out costs nothing.
  changeNumber(event) {
    event.preventDefault()
    if (this.hasPhoneInputTarget) {
      this.phoneInputTarget.readOnly = false
      this.phoneInputTarget.focus()
      this.phoneInputTarget.select?.()
    }
    if (this.hasSendBtnTarget) syncGateReason(this.sendBtnTarget, { ready: true })
    if (this.hasChangeLinkTarget) this.changeLinkTarget.hidden = true
    this.showMessage("Enter the new number, then tap Send Code to verify it.", "success")
  }

  // The badge follows the field: a number other than the verified one reads
  // "Not verified" (the Ready Gate closes on the same comparison).
  phoneEdited() {
    if (!this.hasBadgeTarget) return
    const verified = this.element.dataset.verifiedPhone || ""
    const matches = verified !== "" && phoneDigits(this.phoneInputTarget.value) === phoneDigits(verified)
    this.renderBadge(matches)
  }

  markVerified() {
    this.renderBadge(true)
    if (this.hasPhoneInputTarget) {
      this.element.dataset.verifiedPhone = phoneDigits(this.phoneInputTarget.value)
      // Lock the verified number so the value the server verified can't be
      // silently edited; Change Number unlocks it deliberately.
      this.phoneInputTarget.readOnly = true
    }
    // Hide the whole code section once verified — this also removes the inner
    // Resend link. Leaving it visible let the user re-send (which clears
    // otp_verified_at server-side) while the UI still showed "Verified" and the
    // code input was readOnly, stranding them until a reload.
    if (this.hasCodeSectionTarget) this.codeSectionTarget.classList.add("d-none")
    // Disable the outer Send Code button too — it lives in the phone row, NOT
    // in codeSection, so hiding the section alone left it live. A click would
    // re-send (clearing otp_verified_at server-side) while the badge still read
    // "Verified", desyncing the UI from the server. (The view renders it
    // disabled on fresh loads of an already-verified profile for the same reason.)
    // BUTTON-4: the same reason (tooltip + accessible name) the view paints.
    if (this.hasSendBtnTarget) {
      syncGateReason(this.sendBtnTarget, { ready: false, reason: ALREADY_VERIFIED_REASON })
    }
    if (this.hasChangeLinkTarget) this.changeLinkTarget.hidden = false
    // Let any wrapping controller (e.g. the wizard) react if it wants to.
    this.dispatch("verified")
  }

  renderBadge(verified) {
    if (!this.hasBadgeTarget) return
    this.badgeTarget.className = statusBadgeClass(verified ? "success" : "secondary")
    this.badgeTarget.innerHTML = verified ? '<i class="ph-fill ph-check-circle"></i> Verified' : "Not verified"
  }

  // BUTTON-8: the shared busy state (#19809). It restores the markup it saved
  // when it clears, so sendCode relabels to "Resend Code" only after that.
  setBusy(button, busy) {
    setButtonLoading(button, busy)
  }

  // Replace the button's text while keeping its leading icon.
  setLabel(button, label) {
    const textNode = Array.from(button.childNodes).reverse().find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim())
    if (textNode) textNode.textContent = label
    else button.append(label)
  }

  showMessage(text, type) {
    if (!this.hasMessageTarget) return
    this.messageTarget.textContent = text
    // Pass/fail text is the shared contrast-safe tone (.gl-text-*).
    this.messageTarget.className = `trust-center-otp-message ${type === "success" ? "gl-text-success" : "gl-text-danger"}`
  }
}
